import { z } from 'zod';
import type { Qualitaetsregeln } from './produkte';
import type { Branche } from './branchen';

/**
 * Marktplatz → Produktfelder: Metafeld-Definitionen des Marktplatz-Shops (mp_metafields der PHP-Middleware)
 * und eigene Felder, die Händler ausfüllen dürfen; dazu Qualitätsregeln und Kategorien (Kollektionen).
 */

/** Feldtypen wie in Shopify (Metafeld-Definitionen). Abgeglichene Felder können weitere Typen haben. */
export const PRODUKTFELD_TYPEN = {
  single_line_text_field: 'Text (einzeilig)',
  multi_line_text_field: 'Text (mehrzeilig)',
  number_integer: 'Ganzzahl',
  number_decimal: 'Dezimalzahl',
  boolean: 'Ja/Nein',
  date: 'Datum',
  url: 'Link',
  color: 'Farbe',
  'list.single_line_text_field': 'Mehrfachauswahl',
  jahr: 'Jahr',
} as const;
export type ProduktfeldTyp = keyof typeof PRODUKTFELD_TYPEN;
export const produktfeldTypTitel = (typ: string) => (PRODUKTFELD_TYPEN as Record<string, string>)[typ] ?? typ;

/** Typen, die die KI ergänzen darf (anr_metafelder) */
export const KI_FELDTYPEN = ['single_line_text_field', 'multi_line_text_field', 'number_integer', 'number_decimal', 'boolean', 'list.single_line_text_field', 'jahr'];

/** Typ im Marktplatz-Shop (Shopify-Metafeld): „jahr“ ist dort eine Ganzzahl */
export const shopFeldTyp = (typ: string) => (typ === 'jahr' ? 'number_integer' : typ || 'single_line_text_field');
/** Mehrfachauswahl: Wert ist eine JSON-Liste, wie Shopify sie für list.single_line_text_field erwartet */
export const istMehrfach = (typ: string) => typ.startsWith('list.');

/** Werte einer Mehrfachauswahl lesen (JSON-Liste); andere Felder: ein Wert */
export function feldListe(wert: unknown): string[] {
  if (Array.isArray(wert)) return wert.map(String).filter(Boolean);
  const t = String(wert ?? '').trim();
  if (!t) return [];
  if (t.startsWith('[')) {
    try {
      const l = JSON.parse(t) as unknown;
      if (Array.isArray(l)) return l.map(String).filter(Boolean);
    } catch {
      // keine JSON-Liste – als Text
    }
  }
  return [t];
}

/** Wert zur Anzeige: Mehrfachauswahl mit Komma, Ja/Nein, Zahl mit Einheit */
export function feldwertAnzeige(f: { typ: string; einheit?: string }, wert: unknown): string {
  if (wert === undefined || wert === null || String(wert).trim() === '') return '';
  if (istMehrfach(f.typ)) return feldListe(wert).join(', ');
  if (f.typ === 'boolean') return String(wert) === 'true' ? 'Ja' : String(wert) === 'false' ? 'Nein' : String(wert);
  if (f.typ.startsWith('number')) {
    const n = Number(wert);
    const t = Number.isFinite(n) ? n.toLocaleString('de-DE', { maximumFractionDigits: 3 }) : String(wert);
    return f.einheit ? `${t} ${f.einheit}` : t;
  }
  return String(wert);
}

export const FELD_SCHLUESSEL = /^[A-Za-z0-9_-]{2,64}$/;
export const feldSchluessel = (f: { namespace: string; schluessel: string }) => `${f.namespace}.${f.schluessel}`;

export interface ProduktfeldZeile {
  id: string;
  namespace: string;
  schluessel: string;
  /** namespace.schluessel */
  voll: string;
  name: string;
  beschreibung: string;
  typ: string;
  auswahl: string[];
  /** shop = aus dem Marktplatz-Shop übernommen, eigen = hier angelegt, vorlage = aus der Branchenvorlage */
  herkunft: ProduktfeldHerkunft;
  /** Definition besteht im Marktplatz-Shop (letzter Abgleich) */
  imShop: boolean;
  freigegeben: boolean;
  pflicht: boolean;
  quellen: string[];
  position: number;
  /** Gruppe in Editor und Anzeige, z. B. „Zustand & Lieferumfang“; leer = Weitere Angaben */
  gruppe: string;
  /** Einheit bei Zahlen, z. B. „mm“ */
  einheit: string;
  /** Hinweis für die KI-Anreicherung */
  kiHinweis: string;
  /** Branche der Vorlage (bei herkunft = vorlage) */
  vorlage: Branche | '';
  geaendertAm: string;
}

export type ProduktfeldHerkunft = 'shop' | 'eigen' | 'vorlage';

export interface ProduktfeldSeite {
  eintraege: ProduktfeldZeile[];
  weiter: string | null;
  gesamt: number;
  freigegeben: number;
  pflicht: number;
  /** letzter Abgleich mit dem Marktplatz-Shop */
  abgleichAm: string | null;
  /** Branche des Marktplatzes und Gruppen ihrer Vorlage (Reihenfolge der Anzeige) */
  branche: Branche;
  gruppen: string[];
}

const Liste = (max: number, laenge: number) =>
  z
    .array(z.string().trim().max(laenge))
    .max(max)
    .default([])
    .transform((l) => [...new Set(l.filter(Boolean))]);

/** Eigenes Feld anlegen (Felder aus dem Shop kommen über den Abgleich) */
export const ProduktfeldNeuSchema = z.object({
  namespace: z.string().trim().regex(FELD_SCHLUESSEL, '2–64 Zeichen: Buchstaben, Ziffern, - und _').default('custom'),
  schluessel: z.string().trim().regex(FELD_SCHLUESSEL, '2–64 Zeichen: Buchstaben, Ziffern, - und _'),
  name: z.string().trim().min(1, 'Bitte einen Namen angeben').max(120),
  beschreibung: z.string().trim().max(500).default(''),
  typ: z.enum(Object.keys(PRODUKTFELD_TYPEN) as [ProduktfeldTyp, ...ProduktfeldTyp[]]).default('single_line_text_field'),
  auswahl: Liste(100, 120),
  freigegeben: z.boolean().default(true),
  pflicht: z.boolean().default(false),
  quellen: Liste(20, 120),
  gruppe: z.string().trim().max(60).default(''),
  einheit: z.string().trim().max(20).default(''),
});
export type ProduktfeldNeu = z.input<typeof ProduktfeldNeuSchema>;

/** Ändern: bei Feldern aus dem Shop nur Freigabe, Pflicht, Zuordnung und Reihenfolge */
export const ProduktfeldAendernSchema = z.object({
  name: z.string().trim().min(1, 'Bitte einen Namen angeben').max(120).optional(),
  beschreibung: z.string().trim().max(500).optional(),
  typ: z.enum(Object.keys(PRODUKTFELD_TYPEN) as [ProduktfeldTyp, ...ProduktfeldTyp[]]).optional(),
  auswahl: Liste(100, 120).optional(),
  freigegeben: z.boolean().optional(),
  pflicht: z.boolean().optional(),
  quellen: Liste(20, 120).optional(),
  position: z.number().int().min(0).max(10_000).optional(),
  gruppe: z.string().trim().max(60).optional(),
  einheit: z.string().trim().max(20).optional(),
});
export type ProduktfeldAendern = z.input<typeof ProduktfeldAendernSchema>;

/* ------------------------------------------------------------ Qualitätsregeln */

export const QualitaetsregelnSchema = z.object({
  titelMin: z.coerce.number().int().min(1, 'Mindestens 1').max(100, 'Höchstens 100'),
  beschreibungMin: z.coerce.number().int().min(0, 'Mindestens 0').max(5000, 'Höchstens 5000'),
  bilderMin: z.coerce.number().int().min(0, 'Mindestens 0').max(20, 'Höchstens 20'),
  eanPflicht: z.boolean(),
  skuPflicht: z.boolean(),
  kategoriePflicht: z.boolean(),
  markePflicht: z.boolean().default(false),
  /** Sortiment für KI-Zuordnung und -Texte, z. B. „Mode, Schuhe und Accessoires“ */
  ausrichtung: z.string().trim().max(200).default(''),
});
export type QualitaetsregelnEingabe = z.input<typeof QualitaetsregelnSchema>;

export interface QualitaetsregelnAntwort extends Omit<Qualitaetsregeln, 'pflichtfelder'> {
  ausrichtung: string;
  pflichtfelder: { schluessel: string; name: string }[];
  geaendertAm: string | null;
}

/** Vorgabe ohne eigene Angabe (mp_ausrichtung) */
export const STANDARD_AUSRICHTUNG = 'Mode, Schuhe und Accessoires';

export interface NeuPruefenErgebnis {
  geprueft: number;
  mitMaengeln: number;
  /** Cursor für den nächsten Durchlauf, null = fertig */
  weiter: string | null;
}

/* ------------------------------------------------------------ Feldzuordnung */

const JA = ['true', 'ja', '1', 'yes', 'y', 'x', 'wahr'];
const NEIN = ['false', 'nein', '0', 'no', 'n', 'falsch'];

/** Feld, wie Normalisierung und Zuordnung es brauchen */
export interface NormFeld {
  typ: string;
  auswahl: string[];
  /** Einheit bei Zahlen – Angaben in verwandten Einheiten werden umgerechnet */
  einheit?: string;
  /** Schreibweisen → Auswahlwert (Schlüssel klein geschrieben) */
  werte?: Record<string, string>;
}

/** Umrechnung in die Einheit des Feldes: Faktor je gelieferter Einheit */
const EINHEITEN: Record<string, Record<string, number>> = {
  mm: { mm: 1, cm: 10, m: 1000 },
  cm: { cm: 1, mm: 0.1, m: 100 },
  m: { m: 1, bar: 10, atm: 10, cm: 0.01, ft: 0.3048 },
  g: { g: 1, gr: 1, gramm: 1, kg: 1000 },
  ct: { ct: 1, kt: 1, karat: 1, carat: 1 },
  h: { h: 1, std: 1, stunden: 1, hours: 1, tage: 24, days: 24 },
  monate: { monate: 1, monat: 1, months: 1, jahre: 12, jahr: 12, years: 12, year: 12 },
  kg: { kg: 1, kilogramm: 1, g: 0.001, gramm: 0.001, lbs: 0.453592, lb: 0.453592 },
  l: { l: 1, liter: 1, ltr: 1, ml: 0.001, cl: 0.01 },
  w: { w: 1, watt: 1, kw: 1000 },
  wh: { wh: 1, kwh: 1000 },
  gb: { gb: 1, tb: 1000 },
  zoll: { zoll: 1, '"': 1, inch: 1, in: 1 },
  '% vol': { '% vol': 1, '%': 1, 'vol.-%': 1, 'vol%': 1, '%vol': 1, 'vol.%': 1 },
};

const klein = (t: string) => t.toLowerCase().replace(/\s+/g, ' ').trim();

function auswahlTreffer(f: NormFeld, w: string): string | null {
  const k = klein(w);
  const direkt = f.auswahl.find((a) => klein(a) === k);
  if (direkt) return direkt;
  const syn = f.werte?.[k];
  if (syn && f.auswahl.includes(syn)) return syn;
  return null;
}

function zahlLesen(f: NormFeld, w: string): string | null {
  const m = /^(-?\d+(?:[.,]\d+)?)\s*([^\d\s].*)?$/.exec(w.replace(/\s+/g, ' ').trim());
  if (!m) return null;
  let n = Number(m[1]!.replace(',', '.'));
  const einheit = klein(m[2] ?? '').replace(/\.$/, '');
  if (einheit) {
    const ziel = klein(f.einheit ?? '');
    const faktor = EINHEITEN[ziel]?.[einheit] ?? (einheit === ziel ? 1 : null);
    if (faktor === null || faktor === undefined) return null;
    n = n * faktor;
  }
  if (!Number.isFinite(n)) return null;
  if (f.typ === 'number_integer') {
    if (!Number.isInteger(Math.round(n * 1e6) / 1e6)) return null;
    return String(Math.round(n));
  }
  return String(Math.round(n * 1e6) / 1e6);
}

/**
 * Wert für ein Feld vereinheitlichen (wie anr_produkt): Ja/Nein → true/false, Zahlen mit Punkt (Einheit abgetrennt
 * bzw. umgerechnet), Jahr vierstellig, Auswahl nur aus den erlaubten Werten (auch über Schreibweisen wie „automatic“),
 * Mehrfachauswahl als JSON-Liste. null = Wert passt nicht.
 */
export function feldwertNormalisieren(f: NormFeld, roh: unknown): string | null {
  if (roh === null || roh === undefined) return null;
  if (istMehrfach(f.typ)) {
    let teile: string[];
    if (Array.isArray(roh)) teile = roh.map((x) => String(x ?? ''));
    else if (typeof roh === 'object') return null;
    else {
      const t = String(roh).trim();
      teile = t.startsWith('[') ? feldListe(t) : t.split(/[,;|\n]/);
    }
    const aus: string[] = [];
    for (const t of teile.map((x) => x.trim()).filter(Boolean)) {
      // Element wie der Grundtyp (list.number_integer → number_integer), Auswahl über die Schreibweisen
      const w = f.auswahl.length ? auswahlTreffer(f, t) : feldwertNormalisieren({ ...f, typ: f.typ.slice(5) }, t)?.slice(0, 200) ?? null;
      if (w && !aus.includes(w)) aus.push(w);
    }
    if (!aus.length) return null;
    const json = JSON.stringify(aus.slice(0, 50));
    return json.length <= 2000 ? json : null;
  }
  if (typeof roh === 'object') return null;
  let w = String(roh).trim();
  if (!w) return null;
  if (f.typ === 'boolean') {
    const k = w.toLowerCase();
    if (JA.includes(k)) return 'true';
    if (NEIN.includes(k)) return 'false';
    return null;
  }
  if (f.typ === 'jahr') {
    const m = /(?:^|\D)(1[5-9]\d\d|2\d\d\d)(?:\D|$)/.exec(w);
    if (!m) return null;
    const j = Number(m[1]);
    return j <= new Date().getFullYear() + 1 ? String(j) : null;
  }
  if (f.typ === 'number_integer' || f.typ === 'number_decimal') {
    if (f.einheit !== undefined) return zahlLesen(f, w);
    w = w.replace(/\s+/g, '').replace(',', '.');
    if (!/^-?\d+(\.\d+)?$/.test(w)) return null;
    if (f.typ === 'number_integer' && w.includes('.')) return null;
    return w;
  }
  if (f.auswahl.length) return auswahlTreffer(f, w);
  return w.slice(0, 2000);
}

/**
 * Feldzuordnung aus Händlerdaten: je Feld der erste passende Eintrag aus „namespace.key“, „metafields.namespace.key“,
 * dem Schlüssel allein oder einer der hinterlegten Quellen (Groß-/Kleinschreibung egal).
 */
export function felderZuordnen(
  rohdaten: Record<string, unknown>,
  felder: ({ namespace: string; schluessel: string; quellen: string[] } & NormFeld)[],
): Record<string, string> {
  const kleinMap = new Map<string, unknown>();
  for (const [k, v] of Object.entries(rohdaten)) {
    const n = k.trim().toLowerCase();
    if (!kleinMap.has(n)) kleinMap.set(n, v);
  }
  const aus: Record<string, string> = {};
  for (const f of felder) {
    const voll = feldSchluessel(f);
    const kandidaten = [voll, `metafields.${voll}`, ...f.quellen, f.schluessel];
    for (const k of kandidaten) {
      const w = feldwertNormalisieren(f, kleinMap.get(k.toLowerCase()));
      if (w !== null) {
        aus[voll] = w;
        break;
      }
    }
  }
  return aus;
}

/* ------------------------------------------------------------ Kategorien */

/* Vorgabe ohne eigene Liste (mp_default_categories): Standardkategorien der Branche, siehe kategorieVorlagen.ts */

/** Handles der Startseiten-Kollektion – beim ersten Abgleich nicht als Kategorie genutzt */
export const STARTSEITEN_HANDLES = ['frontpage', 'home-page', 'startseite'];

/** Beispiel-Kollektionen von Shopify-Entwicklungsshops – werden nie Kategorien */
export const DEMO_KOLLEKTIONEN = ['hydrogen', 'liquid', 'automated-collection'];

export interface KategorieZeile {
  id: string;
  titel: string;
  /** Hauptkategorie zur Gliederung (leer = ohne) – nur in der Middleware, nicht im Shop */
  oberkategorie: string;
  handle: string;
  /** Kollektion im Marktplatz-Shop; null = noch nicht im Shop (wird beim Abgleich angelegt) */
  shopId: string | null;
  /** automatische Kollektion (Regeln) – nicht zuweisbar */
  automatisch: boolean;
  /** als Kategorie genutzt */
  aktiv: boolean;
  /** Produkte laut Shop (letzter Abgleich) */
  shopProdukte: number;
  /** Produkte im Marktplatz mit dieser Kategorie (ohne entfernte) */
  produkte: number;
  erstelltAm: string;
}

export interface KategorieSeite {
  eintraege: KategorieZeile[];
  weiter: string | null;
  gesamt: number;
  /** zuweisbar = aktiv und manuell */
  zuweisbar: number;
  /** Produkte ohne Kategorie (ohne entfernte) */
  ohneKategorie: number;
  /** Kategorien in Produkten, die nicht in der Liste stehen (höchstens 50) */
  fremde: { kategorie: string; produkte: number }[];
  abgleichAm: string | null;
  /** keine eigene Liste – es gelten die Standardkategorien der Branche */
  standard: boolean;
  /** Branche des Marktplatzes – bestimmt die Standardkategorien (KATEGORIE_VORLAGEN) */
  branche: Branche;
}

export const KategorieAbfrage = z.object({
  q: z.string().trim().max(80).optional(),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});

const Titel = z
  .string()
  .transform((t) => t.replace(/\s+/g, ' ').trim())
  .pipe(z.string().min(1, 'Bitte einen Namen angeben').max(80, 'Höchstens 80 Zeichen'));

/** Hauptkategorie zur Gliederung – leer = ohne */
const Oberkategorie = z
  .string()
  .transform((t) => t.replace(/\s+/g, ' ').trim())
  .pipe(z.string().max(80, 'Höchstens 80 Zeichen'));

/** Neue Kategorie (mp_kat_neu) – optional gleich Produkten zuordnen */
export const KategorieNeuSchema = z.object({
  titel: Titel,
  oberkategorie: Oberkategorie.default(''),
  ids: z.array(z.string().uuid()).max(500).default([]),
});
export type KategorieNeu = z.input<typeof KategorieNeuSchema>;

export interface KategorieNeuErgebnis {
  id: string;
  titel: string;
  /** gab es schon – wurde (wieder) aktiviert */
  vorhanden: boolean;
  zugeordnet: number;
}

export const KategorieAendernSchema = z.object({
  titel: Titel.optional(),
  /** auch bei Kollektionen aus dem Shop änderbar (nur Gliederung in der Middleware) */
  oberkategorie: Oberkategorie.optional(),
  aktiv: z.boolean().optional(),
});
export type KategorieAendern = z.input<typeof KategorieAendernSchema>;

export const KategorieZuordnenSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
  /** leer = Kategorie entfernen */
  kategorie: z.string().trim().max(80),
});

export const KategorieKiSchema = z.object({
  /** bestimmte Produkte; ohne Angabe die nächsten 50 (ohne Kategorie bzw. neue) */
  ids: z.array(z.string().uuid()).max(100).default([]),
  nurFehlende: z.boolean().default(true),
});
export type KategorieKiEingabe = z.input<typeof KategorieKiSchema>;

export interface KategorieVorschlag {
  titel: string;
  ids: string[];
  produkte: string[];
}

export interface KategorieKiErgebnis {
  geprueft: number;
  zugeordnet: number;
  /** passt nicht zum Sortiment – abgelehnt */
  abgelehnt: number;
  vorschlaege: KategorieVorschlag[];
  /** es gibt weitere Kandidaten */
  weitere: boolean;
}

/* ------------------------------------------------------------ Abgleich mit dem Marktplatz-Shop (vorbereitet) */

/** Metafeld-Definition, wie mp_fetch_metafield_defs sie liefert */
export interface ShopFeldDefinition {
  namespace: string;
  key: string;
  name: string;
  beschreibung: string;
  typ: string;
  auswahl: string[];
}

/** Kollektion, wie kat_sync sie liefert */
export interface ShopKollektion {
  id: string;
  titel: string;
  handle: string;
  automatisch: boolean;
  produkte: number;
}

/** JSON aus einer KI-Antwort lesen (mp_json_from_text): mit oder ohne Markdown-Zaun */
export function jsonAusText(t: string): Record<string, unknown> | null {
  const roh = t.trim().replace(/^```(?:json)?/m, '').replace(/```\s*$/m, '').trim();
  const lesen = (s: string) => {
    try {
      const d = JSON.parse(s) as unknown;
      return d && typeof d === 'object' && !Array.isArray(d) ? (d as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  };
  return lesen(roh) ?? (roh.match(/\{[\s\S]*\}/) ? lesen(roh.match(/\{[\s\S]*\}/)![0]) : null);
}
